import React, { useState, useEffect } from 'react';
import { Mail, Linkedin, Download, Play, Award, ExternalLink, ArrowRight, Sparkles } from 'lucide-react';
import { personalInfo, stats, projects, skills, awards } from '../data/portfolioData';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';
import ProjectCard from '../components/ProjectCard';

const HomePage = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setTimeout(() => setIsLoaded(true), 100);
  }, []);

  const filteredProjects = activeFilter === 'all' ? projects : projects.filter(p => p.category === activeFilter);

  const handleDownloadResume = () => {
    const link = document.createElement('a');
    link.href = '/Ayushi_Chhabra_Resume.pdf';
    link.download = 'Ayushi_Chhabra_Resume.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast({ title: "Resume Download Started" });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          access_key: '7c4e85e7-6396-4310-a201-bf1efe63a185',
          name: formData.name,
          email: formData.email,
          message: formData.message,
          subject: `Portfolio Inquiry from ${formData.name}`,
          from_name: 'Ayushi Chhabra Website'
        })
      });

      const result = await response.json();

      if (result.success) {
        toast({
          title: "Message Sent Successfully! ✓",
          description: "Thank you for reaching out. I'll get back to you soon."
        });
        setFormData({ name: '', email: '', message: '' });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: "Oops! Something went wrong",
        description: "Please try again or email me directly at " + personalInfo.email,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream relative overflow-hidden">
      {/* Floating decorative shapes */}
      <div className="fixed top-20 right-20 w-96 h-96 bg-terracotta opacity-5 rounded-full blur-3xl float-anim pointer-events-none"></div>
      <div className="fixed bottom-20 left-20 w-96 h-96 bg-navy-medium opacity-5 rounded-full blur-3xl float-anim pointer-events-none" style={{animationDelay: '3s'}}></div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-navy-deep/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <div className={isLoaded ? 'reveal-left' : 'opacity-0'}>
              <h1 className="text-xl sm:text-2xl font-display font-bold text-navy-deep">{personalInfo.name}</h1>
              <p className="text-xs text-terracotta font-body uppercase tracking-[0.3em] mt-0.5">{personalInfo.title}</p>
              {personalInfo.subtitle && (
                <p className="text-xs text-navy-deep/70 font-body uppercase tracking-[0.3em] mt-1">{personalInfo.subtitle}</p>
              )}
            </div>
            <div className={`hidden md:flex items-center gap-6 lg:gap-10 ${isLoaded ? 'reveal-bottom delay-200' : 'opacity-0'}`}>
              <a href="#about" className="text-navy-deep/70 hover:text-terracotta transition-colors font-body font-medium underline-grow text-sm lg:text-base">About</a>
              <a href="#work" className="text-navy-deep/70 hover:text-terracotta transition-colors font-body font-medium underline-grow text-sm lg:text-base">Work</a>
              <a href="#contact" className="text-navy-deep/70 hover:text-terracotta transition-colors font-body font-medium underline-grow text-sm lg:text-base">Contact</a>
              <Button 
                onClick={handleDownloadResume}
                className="bg-terracotta hover:bg-terracotta-light text-white font-body font-semibold px-4 lg:px-6 rounded-full glow-on-hover text-sm"
              >
                <Download className="w-3 h-3 lg:w-4 lg:h-4 mr-2" />
                Resume
              </Button>
            </div>
            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <Button 
                onClick={handleDownloadResume}
                className="bg-terracotta hover:bg-terracotta-light text-white font-body font-semibold px-4 py-2 rounded-full text-xs"
              >
                <Download className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero - Editorial Magazine Style */}
      <section className="pt-28 sm:pt-40 pb-20 sm:pb-32 px-4 sm:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 sm:gap-16 items-start">
            {/* Text - 7 columns */}
            <div className="lg:col-span-7 space-y-6 sm:space-y-10">
              <div className={isLoaded ? 'reveal-left delay-300' : 'opacity-0'}>
                <div className="inline-flex items-center gap-2 px-4 sm:px-5 py-2 rounded-full bg-sage/15 border border-sage/30 mb-6 sm:mb-8">
                  <Sparkles className="w-3 sm:w-4 h-3 sm:h-4 text-sage" />
                  <span className="text-xs text-sage font-body font-semibold uppercase tracking-widest">Open to New Opportunities</span>
                </div>
              </div>
              
              <h2 className={`font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black text-navy-deep leading-[0.9] ${isLoaded ? 'reveal-bottom delay-400' : 'opacity-0'}`}>
                {personalInfo.tagline.split(' ').slice(0, 2).join(' ')}<br/>
                <span className="gradient-text">{personalInfo.tagline.split(' ').slice(2, 5).join(' ')}</span><br/>
                <span className="text-navy-medium/60">{personalInfo.tagline.split(' ').slice(5).join(' ')}</span>
              </h2>
              
              <div className={`space-y-6 ${isLoaded ? 'reveal-bottom delay-600' : 'opacity-0'}`}>
                <div>
                  <h3 className="font-display text-2xl sm:text-3xl font-bold text-navy-deep mb-3">Ayuka Group</h3>
                  <p className="text-base sm:text-lg lg:text-xl text-charcoal/70 leading-relaxed font-body max-w-2xl">
                    We are an end-to-end content production company creating high-impact, short-form content for brands looking to grow visibility and reach multicultural audiences across social media platforms and digital channels.
                  </p>
                </div>

                <p className="text-base sm:text-lg lg:text-xl text-charcoal/70 leading-relaxed font-body max-w-2xl">
                  Rooted in South Africa with operations across India, Dubai, and the United States, Ayuka also connects brands with creators worldwide - acting as a trusted intermediary to build authentic partnerships with communities that naturally align with those brands.
                </p>
              </div>
              
              <div className={`flex flex-col sm:flex-row flex-wrap gap-4 ${isLoaded ? 'reveal-bottom delay-700' : 'opacity-0'}`}>
                <Button 
                  size="lg" 
                  className="bg-navy-deep hover:bg-navy-rich text-white font-body font-semibold px-6 sm:px-8 py-4 sm:py-6 text-sm sm:text-base rounded-2xl shadow-2xl shadow-navy-deep/20 interactive-scale w-full sm:w-auto"
                  onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                >
                  Let's Collaborate
                  <ArrowRight className="w-4 sm:w-5 h-4 sm:h-5 ml-2" />
                </Button>
                <Button 
                  size="lg" 
                  className="bg-white hover:bg-cream-dark text-navy-deep font-body font-semibold px-6 sm:px-8 py-4 sm:py-6 text-sm sm:text-base rounded-2xl border-2 border-navy-deep/10 hover:border-terracotta/30 interactive-scale w-full sm:w-auto"
                  onClick={() => document.getElementById('work').scrollIntoView({ behavior: 'smooth' })}
                >
                  <Play className="w-4 sm:w-5 h-4 sm:h-5 mr-2" />
                  View Portfolio
                </Button>
              </div>
            </div>

            {/* Photo - 5 columns - ALIGNED TO TOP OF CRAFTING TEXT */}
            <div className={`lg:col-span-5 lg:mt-16 ${isLoaded ? 'reveal-scale delay-800' : 'opacity-0'}`}>
              <div className="relative">
                <div className="aspect-[4/5] rounded-3xl sm:rounded-[2rem] overflow-hidden shadow-2xl shadow-navy-deep/20 interactive-lift">
                  <img src={personalInfo.image} alt={personalInfo.name} className="w-full h-full object-cover" />
                </div>
                {/* Floating stat */}
                <div className="absolute -bottom-6 -left-6 sm:-bottom-8 sm:-left-8 bg-white p-4 sm:p-6 rounded-2xl sm:rounded-3xl shadow-2xl border border-navy-deep/10">
                  <p className="text-3xl sm:text-4xl lg:text-5xl font-display font-black text-terracotta">10+</p>
                  <p className="text-xs sm:text-sm text-charcoal/60 font-body font-semibold">Years<br/>Experience</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats - Bento Box Grid */}
      <section className="py-12 sm:py-20 px-4 sm:px-8 lg:px-16 bg-gradient-to-b from-cream-dark to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {stats.map((stat, i) => (
              <div key={i} className="bg-white p-4 sm:p-6 lg:p-8 rounded-2xl sm:rounded-3xl shadow-lg shadow-navy-deep/5 border border-navy-deep/5 interactive-lift hover:border-terracotta/20 group">
                <p className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-display font-black text-navy-deep mb-2 sm:mb-3 group-hover:text-terracotta transition-colors">{stat.value}</p>
                <p className="text-xs sm:text-sm font-body font-bold text-charcoal uppercase tracking-wider mb-1">{stat.label}</p>
                <p className="text-xs text-charcoal/50 font-body">{stat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About - Asymmetric Layout */}
      <section id="about" className="py-16 sm:py-24 lg:py-32 px-4 sm:px-8 lg:px-16 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 sm:gap-12 lg:gap-16">
            {/* Content - 8 columns */}
            <div className="lg:col-span-8 space-y-10 sm:space-y-12 lg:space-y-16">
              <div>
                <h3 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-navy-deep mb-8 sm:mb-10 lg:mb-12">
                  About <span className="gradient-text">Me</span>
                </h3>
                <div className="space-y-4 sm:space-y-6 text-base sm:text-lg text-charcoal/80 leading-relaxed font-body">
                  <p>With over a decade of experience in content production and on-camera hosting, I specialize in creating culturally resonant narratives that drive meaningful engagement across TV, digital, and social platforms.</p>
                  <p>My work spans award-winning documentary series, national broadcast commercials, and social impact campaigns for Adobe, Lexus, AARP, LA County, and Bhindi Jewelers, and has reached more than 25 million households worldwide.</p>
                  <p>I bring a rare blend of creative vision, strategic thinking, and operational excellence to every project, from securing $450K+ in media grants to leading cross-functional teams of 25+ professionals.</p>
                </div>
              </div>

              {/* Awards - Premium Cards */}
              <div>
                <h4 className="font-display text-3xl sm:text-4xl font-black text-navy-deep mb-6 sm:mb-8">Recognition</h4>
                <div className="space-y-4 sm:space-y-6">
                  {awards.map((award, i) => (
                    <div key={i} className="bg-cream-dark p-6 sm:p-8 rounded-2xl sm:rounded-3xl border-2 border-navy-deep/5 hover:border-terracotta/30 transition-all duration-300 interactive-scale group">
                      <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                        <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br from-terracotta to-terracotta-light flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform">
                          <Award className="w-6 sm:w-7 lg:w-8 h-6 sm:h-7 lg:h-8 text-white" />
                        </div>
                        <div>
                          <p className="font-display text-xl sm:text-2xl font-bold text-navy-deep mb-2">{award.title}</p>
                          <p className="text-xs sm:text-sm text-terracotta font-body font-bold uppercase tracking-wider mb-2 sm:mb-3">{award.year}</p>
                          <p className="text-sm sm:text-base text-charcoal/70 font-body leading-relaxed">{award.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Expertise Sidebar - FILLS SPACE PERFECTLY */}
            <div className="lg:col-span-4 flex flex-col">
              <div className="bg-white p-6 sm:p-8 lg:p-10 rounded-2xl sm:rounded-3xl lg:rounded-[2rem] shadow-2xl border-2 border-navy-deep/10 flex-1 flex flex-col">
                <h4 className="font-display text-3xl sm:text-4xl font-black text-navy-deep mb-6">Expertise</h4>
                
                {/* Larger text for better fill */}
                <div className="space-y-5 flex-1">
                  {/* Production */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-2 h-2 rounded-full bg-terracotta"></div>
                      <p className="font-body font-black text-navy-deep uppercase tracking-widest text-xs">Production</p>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                      {skills.production.map((skill, i) => (
                        <span key={i} className="px-2.5 py-1.5 rounded-lg bg-terracotta/10 border border-terracotta/20 text-terracotta text-[11px] font-body font-semibold hover:bg-terracotta hover:text-white transition-all cursor-default leading-tight">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Leadership */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-2 h-2 rounded-full bg-navy-deep"></div>
                      <p className="font-body font-black text-navy-deep uppercase tracking-widest text-xs">Leadership</p>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                      {skills.leadership.map((skill, i) => (
                        <span key={i} className="px-2.5 py-1.5 rounded-lg bg-navy-deep/10 border border-navy-deep/20 text-navy-deep text-[11px] font-body font-semibold hover:bg-navy-deep hover:text-white transition-all cursor-default leading-tight">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Creative */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-2 h-2 rounded-full bg-sage"></div>
                      <p className="font-body font-black text-navy-deep uppercase tracking-widest text-xs">Creative</p>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                      {skills.creative.map((skill, i) => (
                        <span key={i} className="px-2.5 py-1.5 rounded-lg bg-sage/10 border border-sage/20 text-sage text-[11px] font-body font-semibold hover:bg-sage hover:text-white transition-all cursor-default leading-tight">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Technical */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-2 h-2 rounded-full bg-sage"></div>
                      <p className="font-body font-black text-navy-deep uppercase tracking-widest text-xs">Technical</p>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                      {skills.technical.map((skill, i) => (
                        <span key={i} className="px-2.5 py-1.5 rounded-lg bg-sage/10 border border-sage/20 text-sage text-[11px] font-body font-semibold hover:bg-sage hover:text-white transition-all cursor-default leading-tight">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Contact links - Push to bottom */}
                <div className="mt-auto pt-6 border-t border-navy-deep/10 space-y-2.5">
                  <a href={`mailto:${personalInfo.email}`} className="flex items-center gap-3 text-charcoal/70 hover:text-terracotta transition-all p-3 rounded-xl hover:bg-cream-dark group">
                    <div className="w-9 h-9 rounded-lg bg-terracotta/10 flex items-center justify-center group-hover:bg-terracotta group-hover:scale-110 transition-all">
                      <Mail className="w-4 h-4 text-terracotta group-hover:text-white transition-colors" />
                    </div>
                    <span className="text-xs font-body font-semibold">{personalInfo.email}</span>
                  </a>
                  <a href={personalInfo.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-charcoal/70 hover:text-navy-deep transition-all p-3 rounded-xl hover:bg-cream-dark group">
                    <div className="w-9 h-9 rounded-lg bg-navy-deep/10 flex items-center justify-center group-hover:bg-navy-deep group-hover:scale-110 transition-all">
                      <Linkedin className="w-4 h-4 text-navy-deep group-hover:text-white transition-colors" />
                    </div>
                    <span className="text-xs font-body font-semibold">LinkedIn Profile</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio - Creative Grid Layout */}
      <section id="work" className="py-16 sm:py-24 lg:py-32 px-4 sm:px-8 lg:px-16 bg-cream">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16 lg:mb-20">
            <h3 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-navy-deep mb-4 sm:mb-6">
              Featured <span className="gradient-text">Work</span>
            </h3>
            <p className="text-base sm:text-lg lg:text-xl text-charcoal/60 max-w-3xl mx-auto font-body px-4">
              Award-winning content production spanning TV broadcast, digital campaigns, and social impact storytelling
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 md:gap-4 mb-12 sm:mb-16 px-2">
            {[
              { label: 'All Projects', value: 'all' },
              { label: 'Broadcast & Digital', value: 'broadcast' },
              { label: 'Print Editorials', value: 'print' },
              { label: 'Social Impact', value: 'social-impact' }
            ].map((filter) => (
              <button
                key={filter.value}
                onClick={() => setActiveFilter(filter.value)}
                className={`px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 rounded-xl sm:rounded-2xl font-body text-xs sm:text-sm font-semibold uppercase tracking-wider transition-all duration-300 ${
                  activeFilter === filter.value
                    ? 'bg-navy-deep text-white shadow-xl shadow-navy-deep/20 scale-105'
                    : 'bg-white text-navy-deep/70 hover:bg-cream-dark border-2 border-navy-deep/10 hover:border-terracotta/30'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {/* Projects Grid - Masonry Style (Natural Heights) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 lg:gap-10 w-full items-start">
            {filteredProjects.map((project) => (
              <div key={project.id} className="w-full">
                <ProjectCard project={project} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-16 sm:py-24 lg:py-32 px-4 sm:px-8 lg:px-16 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <div className="flex justify-center mb-6">
              <img src={personalInfo.ayukaLogo} alt="Ayuka Group" className="h-48 sm:h-60 lg:h-72 w-auto" />
            </div>
            <h3 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-navy-deep mb-4 sm:mb-6 px-4">
              Create with <span className="gradient-text">Ayuka</span>
            </h3>
            <p className="text-base sm:text-lg lg:text-xl text-charcoal/60 font-body px-4">Interested in collaborating? I'd love to hear about your next project.</p>
          </div>

          <div className="bg-cream-dark p-6 sm:p-8 lg:p-12 rounded-2xl sm:rounded-3xl lg:rounded-[2.5rem] shadow-2xl border-2 border-navy-deep/5">
            <form onSubmit={handleContactSubmit} className="space-y-6 sm:space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-xs sm:text-sm font-bold text-navy-deep mb-2 sm:mb-3 uppercase tracking-wider font-body">Name</label>
                  <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required
                    className="bg-white border-2 border-navy-deep/10 focus:border-terracotta text-charcoal placeholder:text-charcoal/30 font-body rounded-xl sm:rounded-2xl h-12 sm:h-14 px-4 sm:px-6 text-sm sm:text-base"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-bold text-navy-deep mb-2 sm:mb-3 uppercase tracking-wider font-body">Email</label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required
                    className="bg-white border-2 border-navy-deep/10 focus:border-terracotta text-charcoal placeholder:text-charcoal/30 font-body rounded-xl sm:rounded-2xl h-12 sm:h-14 px-4 sm:px-6 text-sm sm:text-base"
                    placeholder="your.email@company.com"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-bold text-navy-deep mb-2 sm:mb-3 uppercase tracking-wider font-body">Message</label>
                <Textarea rows={5} value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} required
                  className="bg-white border-2 border-navy-deep/10 focus:border-terracotta text-charcoal placeholder:text-charcoal/30 font-body rounded-xl sm:rounded-2xl p-4 sm:p-6 text-sm sm:text-base"
                  placeholder="Tell me about your project..."
                />
              </div>
              <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="bg-terracotta hover:bg-terracotta-light text-white font-body font-semibold px-6 sm:px-8 py-4 sm:py-6 text-sm sm:text-base rounded-xl sm:rounded-2xl shadow-xl shadow-terracotta/20 glow-on-hover flex-1 md:flex-initial w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Mail className="w-4 sm:w-5 h-4 sm:h-5 mr-2" />
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
                <Button type="button" size="lg" onClick={handleDownloadResume}
                  className="bg-navy-deep hover:bg-navy-rich text-white font-body font-semibold px-6 sm:px-8 py-4 sm:py-6 text-sm sm:text-base rounded-xl sm:rounded-2xl shadow-xl shadow-navy-deep/20 flex-1 md:flex-initial w-full sm:w-auto">
                  <Download className="w-4 sm:w-5 h-4 sm:h-5 mr-2" />
                  Download Resume
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-8 lg:px-16 bg-navy-deep text-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="text-center md:text-left">
              <p className="font-display text-3xl font-bold mb-2">{personalInfo.name}</p>
              <p className="text-sm text-terracotta uppercase tracking-widest font-body">{personalInfo.title}</p>
              <p className="text-sm text-white/50 font-body mt-1">{personalInfo.location}</p>
            </div>
            <div className="flex gap-4">
              <a href={`mailto:${personalInfo.email}`} className="w-14 h-14 bg-white/10 hover:bg-terracotta rounded-2xl flex items-center justify-center transition-all duration-300 hover:scale-110">
                <Mail className="w-6 h-6" />
              </a>
              <a href={personalInfo.linkedin} target="_blank" rel="noopener noreferrer" className="w-14 h-14 bg-white/10 hover:bg-terracotta rounded-2xl flex items-center justify-center transition-all duration-300 hover:scale-110">
                <Linkedin className="w-6 h-6" />
              </a>
            </div>
          </div>
          <div className="text-center text-sm text-white/40 font-body mt-12">
            <p>© {personalInfo.name} · Ayuka Group (Pty) Ltd</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
